import { guid } from "@otto-ec/global-resources/misc";
import type { ChatMessage, PersistentState } from "../../types/PersistentState";
import { isCompletableMessage, isMessageCompleted, type Message } from "../../types/Websocket";
import { getStorage } from "../../lib/CookieConsent";
import { sendCustomError } from "../../lib/ApplicationPerormanceMonitoring";
import { CHAT_MESSAGES_LOCAL_STORAGE_KEY } from "../../config";
import { CURRENT_STATE_VERSION, initialChatMessage, migrateState } from "./persistent-state-migrations";
import { getStateFromStorage, saveStateToStorage } from "./persistent-state-persistence";

const MAX_HISTORY_LENGTH = 500;

/**
 *
 */
export const persistentState: PersistentState = $state(initialState());

/**
 *
 */
const progressMessage = $state<{ value?: string }>({ value: undefined });
export const currentProgressMessage = () => progressMessage.value;

(async function loadInitialState() {
    const stateFromStorage = await loadStateFromStorage();

    if (stateFromStorage) {
        Object.assign(persistentState, stateFromStorage);
    } else {
        applyNewConversationId();
    }
})();

export const isSearchingProducts = $state({ value: false });

/**
 *
 */
export const currentChatMessages = () => persistentState.conversations[0].messages;

/**
 *
 */
export const currentConversationId = () => persistentState.conversations[0].id;

/**
 *
 */
let chatMessagesBuffer: ChatMessage[] = [];

/**
 *
 */
export function createNewChatMessage(newMessage: ChatMessage) {
    const chatMessages = currentChatMessages();

    /*                           */
    if (!newMessage.timestamp) {
        newMessage.timestamp = Date.now();
    }

    /*                                                                                         */
    /*                                         */
    if (newMessage.sender === "user" ||
        newMessage.type === "assistant.recommendations" ||
        newMessage.type === "assistant.product_comparison") {
        chatMessages.push(newMessage);
    } else {
        chatMessagesBuffer.push(newMessage);
    }

    /*                   */
    if (chatMessages.length > MAX_HISTORY_LENGTH) {
        chatMessages.splice(0, chatMessages.length - MAX_HISTORY_LENGTH);
    }
}

/**
 *
 *
 *
 */
export function updateChatWithMessage(message: Message, sender: ChatMessage["sender"] = "assistant") {
    /*                                                                                  */
    if (message.type === "assistant.progress") {
        progressMessage.value = message.message as string;
        return;
    }

    if (message.type === "assistant.guidance_loaded") return;

    const chatMessages = currentChatMessages();

    const latestMessage = chatMessagesBuffer.length > 0 ? chatMessagesBuffer[chatMessagesBuffer.length - 1] : chatMessages[chatMessages.length - 1];
    const lastMessageHasCompleted = !!latestMessage?.message_completed;
    const isNewMessageType = latestMessage?.type !== message.type;

    if (!latestMessage || latestMessage.sender === "user" || isNewMessageType || lastMessageHasCompleted) {
        createNewChatMessage({
            event_id: message.event_id,
            type: message.type,
            message: message.message,
            sender,
            message_completed: isCompletableMessage(message) ? isMessageCompleted(message) : false,
            quick_response_options: message.type === "assistant.message" ? (message.quick_response_options ?? []) : undefined,
            meta: ('meta' in message && message.meta) ? message.meta : undefined
        });
    } else {
        if (typeof latestMessage.message === "string") {
            latestMessage.message += message.message;
            if (message.type === "assistant.message") {
                latestMessage.quick_response_options = [...(latestMessage.quick_response_options ?? []), ...(message.quick_response_options ?? [])];
                latestMessage.meta = {
                    detected_product_base_classes: [...(latestMessage.meta?.detected_product_base_classes || []), ...(message.meta?.detected_product_base_classes || [])],
                    message_signature: latestMessage.meta?.message_signature || message.meta?.message_signature
                };
            }
        }

        if (typeof latestMessage.message === "object") {
            latestMessage.message = message.message;
        }
    }

    if (isCompletableMessage(message) && message.event_completed) {
        /*                                             */
        progressMessage.value = undefined;
        saveStateToStorage(persistentState);
    }
}

export function updateChatWithBufferedMessages() {
    /*                                                  */
    const chatMessages = currentChatMessages();
    if (chatMessagesBuffer.length === 0) return;

    /*                                                                      */
    chatMessages.push(...chatMessagesBuffer);
    chatMessagesBuffer = [];
}

/**
 *
 */
async function loadStateFromStorage(): Promise<PersistentState | undefined> {
    const storage = await getStorage();

    try {
        const loadedState = await getStateFromStorage();

        if (loadedState) {
            return migrateState(loadedState);
        }
    } catch (error) {
        sendCustomError("nucleus-cassandra-client-storage", "Failed to parse chat state from storage", {
            storage: storage.type,
            error: (error as Error).message,
        });
    }
}

/**
 *
 */
function initialState(): PersistentState {
    return {
        version: CURRENT_STATE_VERSION,
        conversations: [
            {
                id: undefined,
                messages: [initialChatMessage()],
            },
        ],
    };
}

/**
 *
 */
function applyNewConversationId() {
    persistentState.conversations[0].id = guid();
}



/**
 *
 */
export async function resetPersistentState() {
    chatMessagesBuffer.length = 0;
    Object.assign(persistentState, initialState());
    applyNewConversationId();
    await clearStorage();
}

/**
 *
 */
export async function clearStorage() {
    const storage = (await getStorage()).instance;
    storage.removeItem(CHAT_MESSAGES_LOCAL_STORAGE_KEY);
}
